import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, ChevronDown, Award, Users, Timer, Building2, CalendarDays, ShieldCheck } from 'lucide-react';
import { STUDIO, PRICING, REVIEWS, VIDEOS, VIDEO_META } from '../data/mock';

/* ---------- Reveal on scroll ---------- */
export function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ---------- Autoplay background/inline video (respects reduced motion) ---------- */
export function PitVideo({ video, className = '', label = 'PITFIT class video' }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      el.removeAttribute('autoplay');
      el.pause();
      return;
    }
    // iOS/Safari: React sets the muted property but not the attribute — both are
    // required for mobile autoplay. Then play imperatively.
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute('muted', '');
    let visible = false;
    const tryPlay = () => {
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) tryPlay();
        else el.pause();
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    // Low Power Mode / blocked autoplay: retry inside the first user gesture
    const onGesture = () => {
      if (visible && el.paused) tryPlay();
    };
    document.addEventListener('touchstart', onGesture, { passive: true });
    document.addEventListener('scroll', onGesture, { passive: true });
    document.addEventListener('click', onGesture, { passive: true });
    return () => {
      io.disconnect();
      document.removeEventListener('touchstart', onGesture);
      document.removeEventListener('scroll', onGesture);
      document.removeEventListener('click', onGesture);
    };
  }, []);
  const webmSrc = video.src.replace(/\.mp4$/, '.webm');
  return (
    <video
      ref={ref}
      className={className}
      poster={video.poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-label={label}
      data-testid="pit-video"
    >
      <source src={webmSrc} type="video/webm" />
      <source src={video.src} type="video/mp4" />
    </video>
  );
}

/* ---------- JSON-LD structured data injector ---------- */
export function JsonLd({ data }) {
  const serialized = JSON.stringify(data);
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = serialized;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [serialized]);
  return null;
}

/* ---------- Mariana Tek embed (one integration per page) ---------- */
export function MarianaEmbed({ route = '/schedule/daily', minHeight = 720, testId = 'mariana-embed' }) {
  const boxRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const remount = () => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el || el.childElementCount > 0) return true;
      if (window.MTIntegrations && typeof window.MTIntegrations.render === 'function') {
        try {
          window.MTIntegrations.render(`[data-testid="${testId}"]`);
        } catch (err) {
          console.warn('MarianaEmbed: render skipped (already mounted)', err);
        }
        return true;
      }
      return false;
    };
    // First load: inject the loader once (it auto-scans on execution).
    // SPA navigation: loader already present, re-mount onto the fresh container.
    if (!remount() && !document.querySelector('script[data-mariana-loader]')) {
      ['polyfills', 'js'].forEach((name) => {
        const s = document.createElement('script');
        s.src = `https://pitfitworkout.marianaiframes.com/${name}`;
        s.setAttribute('data-timestamp', +new Date());
        s.setAttribute('data-mariana-loader', 'true');
        document.body.appendChild(s);
      });
    }
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (boxRef.current && boxRef.current.querySelector('iframe')) {
        setStatus('ready');
        clearInterval(iv);
      } else if (tries >= 40) {
        setStatus('failed');
        clearInterval(iv);
      }
    }, 500);
    return () => clearInterval(iv);
  }, [testId]);

  return (
    <div className="glass-card overflow-hidden p-2 md:p-3" style={{ borderColor: 'rgba(125,167,255,0.3)' }} data-testid={`${testId}-card`}>
      <div ref={boxRef} className={`relative rounded-xl overflow-hidden ${status === 'ready' ? 'bg-white' : ''}`} style={{ minHeight }}>
        <div data-mariana-integrations={route} data-testid={testId} />
        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-testid={`${testId}-loading`}>
            <span className="pulse-dot" />
            <p className="font-mono-pf text-[12px] uppercase tracking-[0.14em] text-muted-pf">Loading live schedule…</p>
          </div>
        )}
        {status === 'failed' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid={`${testId}-error`}>
            <p className="text-white font-display font-semibold text-lg">The live board didn't load.</p>
            <p className="text-muted-pf text-[14px] max-w-md">Refresh the page to try again, or use the quick booking form and we'll confirm your spot by email.</p>
            <Link to="/book" className="btn-primary !min-h-[44px]">Use the Quick Form <ArrowRight size={15} /></Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- VideoObject schema for embedded videos (Google video indexing) ---------- */
export function VideoSeo({ keys, uploadDate = '2026-07-23' }) {
  const site = 'https://pitfitworkout.com';
  const graph = keys
    .filter((k) => VIDEOS[k] && VIDEO_META[k])
    .map((k) => ({
      '@type': 'VideoObject',
      name: VIDEO_META[k].name,
      description: VIDEO_META[k].description,
      thumbnailUrl: `${site}${VIDEOS[k].poster}`,
      contentUrl: `${site}${VIDEOS[k].src}`,
      uploadDate,
      publisher: { '@type': 'Organization', name: 'PITFIT', url: site },
    }));
  if (!graph.length) return null;
  return <JsonLd data={{ '@context': 'https://schema.org', '@graph': graph }} />;
}


/* ---------- Section header ---------- */
export function SectionHeader({ tag, title, sub, center = false }) {
  return (
    <div className={`max-w-2xl ${center ? 'mx-auto text-center' : ''} mb-12`}>
      {tag && <p className="mono-label mb-4">{tag}</p>}
      <h2 className="h-section text-white">{title}</h2>
      {sub && <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">{sub}</p>}
    </div>
  );
}

/* ---------- Proof strip ---------- */
export function ProofStrip() {
  const items = [
    { id: 'rating', node: <span className="flex items-center gap-1.5"><Star size={14} fill="currentColor" style={{ color: 'var(--pf-brand2)' }} /> {STUDIO.rating} avg · {STUDIO.reviewCount}+ reviews</span> },
    { id: 'dmag', node: <span>Featured in D Magazine</span> },
    { id: 'papercity', node: <span>PaperCity “Best Gyms”</span> },
    { id: 'founder', node: <span>Founder-led coaching team</span> },
    { id: 'classes', node: <span>Right-sized classes, real coaching</span> },
  ];
  return (
    <div className="border-y border-[rgba(148,170,220,0.12)]" style={{ background: 'var(--pf-bg2)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
        {items.map((it) => (
          <div key={it.id} className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf flex items-center">
            {it.node}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Class card ---------- */
export function ClassCard({ cls, day }) {
  const isHyrox = cls.name.toUpperCase().includes('HYROX');
  const is45 = cls.name.includes('45');
  const durLabel = is45 ? '45 min' : '60 min';
  const typeLabel = isHyrox ? 'HYROX training' : 'non-stop HIIT';
  return (
    <div className="glass-card glass-card-hover p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono-pf text-[11px] uppercase tracking-[0.14em] text-muted-pf">{day ? `${day.slice(0, 3).toUpperCase()} · ` : ''}{cls.time}</span>
        <span className="font-mono-pf text-[11px] uppercase tracking-[0.12em]" style={{ color: 'var(--pf-brand2)' }}>{durLabel}</span>
      </div>
      <h4 className="font-display font-semibold text-white text-lg leading-snug">{cls.name}</h4>
      <p className="text-muted-pf text-[14px]">{typeLabel} · Coach {cls.coach}</p>
      <Link
        to={`/book?class=${encodeURIComponent(`${day || ''} · ${cls.time} · ${cls.name}`)}`}
        className="btn-ghost hover:!border-[rgba(125,167,255,0.8)] !min-h-[44px] !py-2 mt-1 text-[15px] justify-center"
      >
        Reserve <ArrowRight size={15} />
      </Link>
    </div>
  );
}

/* ---------- Pricing tiers ---------- */
export function PricingTiers({ compact = false }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
      {PRICING.map((tier, i) => (
        <Reveal key={tier.id} delay={i * 90} className="h-full">
          <div
            className={`glass-card h-full p-7 flex flex-col relative ${tier.popular ? 'md:-translate-y-3' : ''}`}
            style={tier.popular ? { borderColor: 'rgba(125,167,255,0.55)', boxShadow: '0 0 34px rgba(70,127,247,0.18)' } : {}}
          >
            {tier.popular && (
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 font-mono-pf text-[11px] uppercase tracking-[0.14em] text-white px-4 py-1.5 rounded-full" style={{ background: 'var(--pf-grad)' }}>
                Most Popular
              </span>
            )}
            <p className="mono-label">{tier.name}</p>
            <div className="flex items-baseline gap-1 mt-4">
              <span className="font-display font-bold text-5xl text-white">${tier.price}</span>
              <span className="text-muted-pf font-mono-pf text-[13px]">{tier.unit}</span>
            </div>
            <ul className="mt-6 space-y-3 flex-1">
              {(compact ? tier.features.slice(0, 3) : tier.features).map((f) => (
                <li key={f} className="flex gap-2.5 text-[15px] text-muted-pf">
                  <span className="mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--pf-brand)' }} />
                  {f}
                </li>
              ))}
            </ul>
            <Link to="/buy" className={`${tier.popular ? 'btn-primary' : 'btn-ghost'} w-full mt-7 justify-center`}>
              {tier.cta}
            </Link>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ---------- Review card + wall ---------- */
export function ReviewCard({ review }) {
  return (
    <div className="glass-card glass-card-hover p-6 flex flex-col gap-4 h-full">
      <div className="flex gap-1" aria-label={`${review.stars} star review`}>
        {Array.from({ length: review.stars }).map((_, i) => (
          <Star key={i} size={15} fill="currentColor" style={{ color: 'var(--pf-brand2)' }} />
        ))}
      </div>
      <p className="text-[15px] leading-relaxed text-[#cdd5e4] flex-1">“{review.text}”</p>
      <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf">— {review.name} · via {review.source}</p>
    </div>
  );
}

export function ReviewWall({ limit = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {REVIEWS.slice(0, limit).map((r, i) => (
        <Reveal key={r.name} delay={i * 70}>
          <ReviewCard review={r} />
        </Reveal>
      ))}
    </div>
  );
}

/* ---------- FAQ accordion ---------- */
export function FAQAccordion({ faqs }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => {
        const open = openIdx === i;
        return (
          <div key={f.q} className="glass-card overflow-hidden">
            <button
              className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 min-h-[48px]"
              onClick={() => setOpenIdx(open ? -1 : i)}
              aria-expanded={open}
            >
              <span className="font-display font-semibold text-white text-[17px]">{f.q}</span>
              <ChevronDown size={19} className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--pf-brand2)' }} />
            </button>
            <div className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden">
                <p className="px-6 pb-6 text-muted-pf text-[15px] leading-relaxed">{f.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Risk-reversal close CTA ---------- */
export function CloseCTA({ title = 'Your first class is $15.', sub = 'No membership. No commitment. Just show up 15 minutes early, and get in the PIT.' }) {
  return (
    <section className="section-pad">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="glass-card relative overflow-hidden text-center px-6 py-14 md:py-16 grain" style={{ borderColor: 'rgba(125,167,255,0.3)' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(70,127,247,0.16), transparent 65%)' }} />
            <div className="relative">
              <div className="flex justify-center mb-5">
                <ShieldCheck size={30} style={{ color: 'var(--pf-brand2)' }} />
              </div>
              <h2 className="h-section text-white">{title}</h2>
              <p className="text-muted-pf mt-4 text-[17px] max-w-xl mx-auto">{sub}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Link to="/book" className="btn-primary">Book a Class <ArrowRight size={17} /></Link>
                <Link to="/hiit-workout/first-class" className="btn-ghost">What to Expect</Link>
              </div>
              <p className="font-mono-pf text-[12px] uppercase tracking-[0.14em] text-muted-pf mt-6">$15 first class · No membership · No commitment</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Page hero (interior pages) ---------- */
export function PageHero({ tag, title, sub, img, video }) {
  let backdrop = null;
  if (video) {
    backdrop = (
      <>
        <PitVideo video={video} className="absolute inset-0 w-full h-full object-cover" label="Background video of a PITFIT HIIT class" />
        <div className="absolute inset-0 hero-overlay" />
      </>
    );
  } else if (img) {
    backdrop = (
      <>
        <img src={img} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 hero-overlay" />
      </>
    );
  }
  return (
    <section className="relative overflow-hidden">
      {backdrop}
      <div className={`relative max-w-7xl mx-auto px-5 lg:px-8 ${img || video ? 'py-28 md:py-36' : 'pt-16 md:pt-24 pb-10'}`}>
        <Reveal>
          {tag && <p className="mono-label mb-4">{tag}</p>}
          <h1 className="h-display text-white max-w-3xl">{title}</h1>
          {sub && <p className="text-[#c3cbdc] mt-5 text-lg max-w-2xl leading-relaxed">{sub}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export const ICONS = { Timer, Users, Award, Building2, CalendarDays, Star };
